/**
 * 会议快捷键。
 *
 * 主流会议软件（腾讯会议、Zoom）都有一套肌肉记忆级的快捷键，
 * 老系统一个都没有 —— 开关麦克风必须把鼠标挪到工具栏点一下。
 *
 * 两条硬性要求：
 *
 *   1. 在输入框里打字时必须整体失效。
 *      聊天框里敲空格若被解读成「临时开麦」，等于每打一个词就漏出一次声音。
 *
 *   2. 「按住说话」要按下即开、松开即关，且要防住按键自动重复 ——
 *      按住不放时浏览器会连发 keydown，不去重就会反复推流。
 */

import { useEffect, useRef } from 'react'

/**
 * 快捷键回调集合。缺省的项表示该功能当前不可用（比如学员没有共享权限时不传 toggleShare），
 * 按键会被忽略而不是报错。
 *
 * 键位照腾讯会议：会议软件的快捷键是肌肉记忆，改一个键位比不做更糟。
 */
export interface HotkeyActions {
  /** Ctrl/Cmd + D：开关麦克风 */
  toggleMic?: () => void
  /** Ctrl/Cmd + E：开关摄像头 */
  toggleCam?: () => void
  /** Ctrl/Cmd + Shift + S：开始/停止共享屏幕 */
  toggleShare?: () => void
  /** Ctrl/Cmd + Shift + H：开关聊天面板 */
  toggleChat?: () => void
  /** Ctrl/Cmd + U：开关成员列表 */
  toggleMembers?: () => void
  /** Ctrl/Cmd + Shift + K：举手 / 放下手 */
  toggleHand?: () => void
  /** Shift + F：全屏切换（不带 Ctrl，与腾讯会议一致） */
  toggleFull?: () => void
  /** 按住空格临时说话：按下 true、松开 false。 */
  pushToTalk?: (talking: boolean) => void
}

/** 判断焦点是否落在可输入元素上。 */
function isTyping(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable) return true
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * 绑定全局快捷键，返回解绑函数。
 *
 * 与 React 解耦是为了能直接测：这些行为（自动重复去重、切窗口复位）
 * 用事件就能验证，不必为此拖进一整套组件测试工具。
 * getActions 每次现取，调用方换了回调也不用重绑。
 */
export function bindHotkeys(getActions: () => HotkeyActions): () => void {
  const ref = { get current() { return getActions() } }
  {
    // 记住空格是否已经按下，忽略自动重复。
    let talking = false

    const onKeyDown = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return
      const mod = e.ctrlKey || e.metaKey

      if (mod && !e.shiftKey && !e.altKey && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault()
        ref.current.toggleMic?.()
        return
      }
      if (mod && !e.shiftKey && !e.altKey && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault()
        ref.current.toggleCam?.()
        return
      }
      if (mod && !e.shiftKey && !e.altKey && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault()
        ref.current.toggleMembers?.()
        return
      }
      // 带 Shift 的三个。用 e.code 而不是 e.key 判断字母：
      // 按下 Shift 时 e.key 在部分键盘布局下会变成别的字符，而 code 恒定是物理键位。
      if (mod && e.shiftKey && !e.altKey) {
        if (e.code === 'KeyS') {
          e.preventDefault()
          ref.current.toggleShare?.()
          return
        }
        if (e.code === 'KeyH') {
          e.preventDefault()
          ref.current.toggleChat?.()
          return
        }
        if (e.code === 'KeyK') {
          e.preventDefault()
          ref.current.toggleHand?.()
          return
        }
      }
      // 全屏不带 Ctrl（与腾讯会议一致）。因此必须排除任何修饰键，
      // 否则 Ctrl+Shift+F 之类的浏览器快捷键会被我们抢走。
      if (!mod && e.shiftKey && !e.altKey && e.code === 'KeyF') {
        e.preventDefault()
        ref.current.toggleFull?.()
        return
      }
      if (e.code === 'Space' && !mod && !e.altKey && ref.current.pushToTalk) {
        // 空格默认会滚动页面，按住说话时页面跟着跳非常干扰。
        e.preventDefault()
        if (talking) return
        talking = true
        ref.current.pushToTalk(true)
      }
    }

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !talking) return
      talking = false
      ref.current.pushToTalk?.(false)
    }

    // 切走窗口时若还按着空格，keyup 会落在别处，麦克风就一直开着。
    const onBlur = () => {
      if (!talking) return
      talking = false
      ref.current.pushToTalk?.(false)
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      // 卸载时若还按着空格，keyup 再也不会来了，必须在这里关掉。
      onBlur()
    }
  }
}

/** 注册全局会议快捷键。 */
export function useHotkeys(actions: HotkeyActions): void {
  // 用 ref 存回调：actions 每次渲染都是新对象，
  // 直接进依赖数组会导致每帧反复解绑重绑，按住空格时正好丢事件。
  const ref = useRef(actions)
  ref.current = actions
  useEffect(() => bindHotkeys(() => ref.current), [])
}
